<script setup>
import { ref } from "vue";
import ArgonButton from "@/components/ArgonButton.vue";
import Swal from "sweetalert2";
import UpdatePassword from "@/views/auth/domain/update-password";

// Props para recibir el token 
const props = defineProps({
  token: String, 
});

const emit = defineEmits();

// refs para manejar los datos del formulario
const newPassword = ref("");
const confirmPassword = ref("");
const message = ref("");
const errors = ref([]);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// funcion para validar la contraseña
const validatePassword = (password) => {
  const requirements = [
    { regex: /.{8,}/, message: "Debe tener al menos 8 caracteres." },
    { regex: /[A-Z]/, message: "Debe contener al menos una letra mayúscula." },
    { regex: /[a-z]/, message: "Debe contener al menos una letra minúscula." },
    { regex: /\d/, message: "Debe contener al menos un número." },
    { regex: /[^A-Za-z0-9]/, message: "Debe contener al menos un caracter especial." },
  ];
  
  return requirements.filter(req => !req.regex.test(password)).map(req => req.message);
};

// Update de contraseña
const handleUpdatePassword = async () => {
  errors.value = [];
  message.value = "";
  
  // Revisa validaciones
  errors.value = validatePassword(newPassword.value);
  if (errors.value.length > 0) {
   
    return;
  }

  // Revisa si las contraseñas coinciden
  if (newPassword.value !== confirmPassword.value) {
    Swal.fire({
      icon: "warning",
      title: "Advertencia",
      text: "Las contraseñas no coinciden.",
      customClass: { confirmButton: "btn bg-gradient-warning" },
      buttonsStyling: false,
    });
    return;
  }

  try {
    await UpdatePassword(props.token, newPassword.value);
    Swal.fire({
      icon: "success",
      title: "Actualizado",
      text: "La contraseña fue actualizada exitosamente.",
      customClass: { confirmButton: "btn bg-gradient-success" },
      buttonsStyling: false,
    });
    closeModal();
    location.reload();
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Ocurrió un error al actualizar la contraseña.",
      customClass: { confirmButton: "btn bg-gradient-danger" },
      buttonsStyling: false,
    });
  }
};

const closeModal = () => {
  emit("close");
  message.value = "";
  errors.value = [];
  newPassword.value = "";
  confirmPassword.value = "";
};
</script>

<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-content card">
      <div class="modal-header">
        <h5 class="modal-title">Actualizar Contraseña</h5>
        <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
      </div>
      <form @submit.prevent="handleUpdatePassword">
        <div class="modal-body">
          <label>Nueva Contraseña</label>
          <div class="position-relative">
            <input
              class="form-control mb-3"
              v-model="newPassword"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Nueva Contraseña"
            />
            <span class="toggle-password" @click="showPassword = !showPassword">
              <i :class="showPassword ? 'fas fa-eye-slash bg-white' : 'far fa-eye bg-white'"></i>
            </span>
          </div>
          <label>Confirmar Nueva Contraseña</label>
          <div class="position-relative">
            <input
              class="form-control mb-3"
              v-model="confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              placeholder="Confirmar Nueva Contraseña"
            />
            <span class="toggle-password" @click="showConfirmPassword = !showConfirmPassword">
              <i :class="showConfirmPassword ? 'fas fa-eye-slash bg-white' : 'far fa-eye bg-white'"></i>
            </span>
          </div>
          <div v-if="errors.length" class="alert alert-danger">
            <ul>
              <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
            </ul>
          </div>
        </div>
        <small class="text-muted"> *La contraseña debe contener: </small>
                <div class="row list-row w-100">
                  <div class="col-lg-6 col-12 ps-0">
                    <ul class="password-requirements">
                      <li>8 caracteres como mínimo</li>
                      <li>Una letra mayúscula.</li>
                      <li>Una letra minúscula.</li>
                    </ul>
                  </div>
                  <div class="col-lg-6 col-12 ps-0">
                    <ul class="password-requirements">
                      <li>Un número.</li>
                      <li>Un carácter especial (ej. !, @, #, etc.).</li>
                    </ul>
                  </div>
                </div>
        <div class="modal-footer">
          <argon-button type="submit" color="dark" variant="gradient">
            Actualizar
          </argon-button>
        </div>
      </form>
    </div>
  </div>
</template>



<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 24, 6, 0.384);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  padding: 20px;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 10px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.toggle-password {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 1.2rem;
  color: #6c757d;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #000;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.btn-close::before {
  content: "×";
  font-weight: bold;
}

.btn-close:hover::before {
  color: #ff0000;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-width: 400px;
    padding: 15px;
  }

  .modal-header {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .modal-title {
    font-size: 1.2rem;
  }

  .btn-close {
    font-size: 1.2rem;
    align-self: flex-end;
  }

  .modal-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .argon-button {
    width: 100%;
  }

  .toggle-password {
    font-size: 1rem;
    right: 10px;
  }

  input {
    font-size: 1rem;
    padding: 10px;
  }

  
}
.password-requirements {
  list-style-type: none;
  /* Remove the default bullet */
  padding-left: 25px;
  margin-top: 2px;
}

.password-requirements li {
  font-size: 0.75rem;
  /* Smaller font size for the list items */
  color: #6c757d;
  /* Muted color */
  margin-bottom: 2px;
  display: flex;
  align-items: center;
}

.password-requirements li::before {
  content: "•";
  color: #2dce89;
  font-size: 1.2rem;
  margin-right: 8px;
  display: inline-block;
}
</style>
